/**
 * Журнал: одно место, куда пишут все части показа экрана.
 *
 * Заведён потому, что показ у одного человека не запускался, а сказать о нём
 * было нечего. Путь идёт через четыре дома — страница, оболочка Tauri,
 * программа захвата, точка приёма LiveKit, — и до этого каждый молчал
 * по-своему: страница глотала отказы в `catch {}` и возвращала «нет
 * захвата», оболочка писала `println!` в консоль, которой в собранном
 * приложении нет вовсе (`windows_subsystem = "windows"`), а программа захвата
 * писала в поток ошибок, который оболочка читала и печатала туда же, в
 * никуда. Три подробных рассказа о поломке — и ни одного слушателя.
 *
 * Отсюда два выхода у каждой строки:
 *
 * - **консоль отладчика**. F12 в собранном приложении открывается намеренно
 *   (см. признак `devtools` в `src-tauri/Cargo.toml`), и «покажи, что в
 *   консоли» — самый короткий путь к причине.
 * - **файл**. Консоль надо успеть открыть до поломки, а файл лежит и после;
 *   попросить его переслать можно и через день. Пишет его оболочка: страница
 *   лишь отдаёт ей строку, потому что своего доступа к файлам у неё нет.
 *
 * В вебе второго выхода нет и не будет: там нет ни оболочки, ни захвата, а
 * ронять запись в никуда незачем — строки просто остаются в памяти, и их
 * забирают через `airel.journal()`.
 *
 * Тайна тут одна — ключ потока в адресе WHIP. Он проходит через журнал
 * насквозь, поэтому адреса пишутся через `mask`.
 */

/** Насколько длинную память держим. Хватает на десяток попыток показа. */
const KEEP = 2000;

export type Level = "info" | "warn" | "error";

const kept: string[] = [];
let revision = 0;
const listeners = new Set<() => void>();

/** Отложенное оповещение: залп строк подряд — обычное дело при запуске. */
let pending = false;

function announce() {
  revision += 1;

  if (pending) return;
  pending = true;

  queueMicrotask(() => {
    pending = false;
    for (const listener of listeners) listener();
  });
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Отпечаток памяти для `useSyncExternalStore`.
 *
 * Число, а не массив: массив пришлось бы пересоздавать на каждую строку,
 * иначе подписчик не заметит изменения, — а строк за секунду бывает полсотни.
 */
export function version(): number {
  return revision;
}

export function lines(): readonly string[] {
  return kept;
}

export function text(): string {
  return kept.join("\n");
}

/*
 * Отправка в оболочку.
 *
 * `window.airelShell` кладёт preload Electron и синхронно — очередь и ленивая
 * загрузка модуля больше не нужны. В вебе его нет, и строка просто остаётся в
 * памяти; файла там всё равно не бывает.
 */
function inShell(): boolean {
  return typeof window !== "undefined" && !!window.airelShell;
}

function ship(line: string) {
  window.airelShell?.journal.log(line);
}

/** Время суток с миллисекундами: день в имени файла, а здесь он лишний. */
function stamp(): string {
  const now = new Date();
  const two = (value: number) => String(value).padStart(2, "0");

  return `${two(now.getHours())}:${two(now.getMinutes())}:${two(now.getSeconds())}.${String(
    now.getMilliseconds(),
  ).padStart(3, "0")}`;
}

function tail(data?: Record<string, unknown>): string {
  if (!data) return "";

  return Object.entries(data)
    .filter(([, value]) => value !== undefined)
    .map(([name, value]) => {
      const shown =
        typeof value === "string" || typeof value === "number" || typeof value === "boolean"
          ? String(value)
          : JSON.stringify(value);

      return ` ${name}=${shown}`;
    })
    .join("");
}

/**
 * Пишет строку.
 *
 * `local` — строка пришла не отсюда, а из оболочки или программы захвата.
 * Такие в файл не возвращаются: они там уже есть, и второй раз их писать
 * значит удваивать журнал ровно в той его части, которая нужнее всего.
 */
function put(level: Level, scope: string, message: string, data?: Record<string, unknown>, local = false) {
  const line = `${stamp()} ${level.padEnd(5)} ${scope.padEnd(8)} ${message}${tail(data)}`;

  kept.push(line);
  if (kept.length > KEEP) kept.splice(0, kept.length - KEEP);

  const speak = level === "error" ? console.error : level === "warn" ? console.warn : console.info;
  if (data) speak(`[airel] ${line}`, data);
  else speak(`[airel] ${line}`);

  if (!local) ship(line);

  announce();
}

export function note(scope: string, message: string, data?: Record<string, unknown>) {
  put("info", scope, message, data);
}

export function warn(scope: string, message: string, data?: Record<string, unknown>) {
  put("warn", scope, message, data);
}

/**
 * Отказ.
 *
 * Причина разбирается здесь, а не на каждом месте вызова: `catch` ловит
 * `unknown`, и «что это было» одинаково выясняется во всех тридцати местах.
 */
export function fail(scope: string, message: string, error: unknown, data?: Record<string, unknown>) {
  const because =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === "string"
        ? error
        : JSON.stringify(error);

  put("error", scope, message, { ...data, причина: because });
}

/**
 * Прячет ключ потока в адресе.
 *
 * Адрес WHIP — это `{адрес}/{ключ}`, и ключ пускает вещать кого угодно.
 * Журнал уходит в переписку, поэтому наружу выходит только хвост в три знака:
 * его хватает, чтобы понять, что ключ вообще другой, и мало, чтобы им
 * воспользоваться.
 */
export function mask(url: string): string {
  const cut = url.lastIndexOf("/");
  if (cut < 0) return url;

  const key = url.slice(cut + 1);
  const shown = key.length > 3 ? `…${key.slice(-3)}` : "…";

  return `${url.slice(0, cut + 1)}${shown}`;
}

/** Где лежит файл журнала. Пусто в вебе — там его нет. */
export async function path(): Promise<string | null> {
  if (!inShell()) return null;

  try {
    return (await window.airelShell?.journal.path()) ?? null;
  } catch {
    return null;
  }
}

/** Открывает папку с журналом в проводнике. */
export async function reveal(): Promise<void> {
  if (!inShell()) return;

  try {
    await window.airelShell?.journal.reveal();
  } catch (error) {
    fail("журнал", "папку не открыть", error);
  }
}

let held = false;

/**
 * Включает журнал на всё время работы.
 *
 * Зовётся из `main.tsx`, до первой отрисовки: несобранное окно и упавший
 * модуль — тоже происшествия, и ловить их надо раньше, чем появится тот, кто
 * ими интересуется.
 *
 * Снятия нет намеренно: журнал живёт столько же, сколько страница.
 */
export function holdJournal() {
  if (held) return;
  held = true;

  note("старт", "журнал открыт", {
    оболочка: inShell(),
    адрес: typeof location === "undefined" ? "?" : location.href,
    клиент: typeof navigator === "undefined" ? "?" : navigator.userAgent,
  });

  if (typeof window !== "undefined") {
    window.addEventListener("error", (event) => {
      put("error", "окно", "необработанная ошибка", {
        где: `${event.filename}:${event.lineno}`,
        что: event.message,
      });
    });

    window.addEventListener("unhandledrejection", (event) => {
      fail("окно", "обещание отклонено без обработчика", event.reason);
    });
  }

  /*
   * Строки программы захвата.
   *
   * Она пишет в поток ошибок, оболочка его читает и пересылает сюда
   * событием. Смысл в том, чтобы всё лежало в одном месте: без этого
   * «страница попросила» и «программа отказала» находятся в разных окнах и
   * с разными часами.
   */
  if (!inShell()) return;

  window.airelShell?.capture.onLog((entry) => {
    const { level, scope, line } = entry;
    const shown: Level = level === "error" ? "error" : level === "warn" ? "warn" : "info";

    put(shown, scope, line, undefined, true);
  });
}
